/* eslint @typescript-eslint/strict-boolean-expressions: off */

import Link from "next/link";

import { addToastNotification } from "shared/Toasts/toastNotificationStore";
import {
  SupportedExecutionTypes,
  callExecuteQueryV3,
} from "shared/executeQuery/callExecuteQueryV3";

import type { Parameter } from "src/utils/parameters/types";
import type { Session } from "src/utils/users/types";
import { ApolloError } from "@apollo/client";
import * as Sentry from "@sentry/react";
import type { ActiveContext } from "shared/ContextSwitcher/store";
import type { SupportedPublicPerformanceTierKey } from "shared/SelectPerformance/useGetPerformanceTiers";

interface Args {
  queryId: number;
  parameters: Parameter[];
  requestContext: { session?: Session; apiKey?: string };
  activeContext: ActiveContext;
  executionType: SupportedExecutionTypes;
  performance: SupportedPublicPerformanceTierKey | undefined;
  metadata?: Record<string, any>;
}

export const executeQueryAndAddToastOnError = async (args: Args) => {
  const {
    queryId,
    parameters,
    requestContext,
    activeContext,
    executionType,
    performance,
    metadata,
  } = args;

  try {
    return await callExecuteQueryV3({
      queryId,
      parameters,
      requestContext,
      executionContext: activeContext,
      executionType,
      performance,
      metadata,
    });
  } catch (error) {
    if (error instanceof ApolloError) {
      const errorCode = error.graphQLErrors?.[0].extensions?.code;
      const href = getSubscriptionSettingsLink(activeContext);

      switch (errorCode) {
        case "not_enough_credits":
          addToastNotification({
            level: "error",
            title: "You've reached the credit limit.",
            actions: (
              <>
                <Link href={href}>Upgrade plan</Link>
                <Link
                  href={href}
                  // ButtonTextOnly
                  style={{ color: "var(--link--tertiary--default)" }}
                >
                  Increase credit limit
                </Link>
              </>
            ),
          });
          throw error;

        // Handle these like any other error:
        case "unsupported_performance": // Selected performance does not exist
        case "unsupported_service_tier": // Selected performance is not supported for current datset
      }
    }

    if (error instanceof Error) {
      error.message = "Execute query: " + error.message;
    }
    Sentry.captureException(error, {
      extra: {
        executor: activeContext
          ? `${activeContext.type}_${activeContext.id}`
          : "anonymous",
        queryId,
        performance,
      },
    });

    const description = error instanceof Error ? error.message : String(error);

    addToastNotification({
      level: "error",
      title: "Query execution failed",
      description,
    });

    throw error;
  }
};

function getSubscriptionSettingsLink(activeContext: ActiveContext) {
  if (!activeContext) {
    return "/pricing";
  }

  switch (activeContext.type) {
    case "user":
      return "/settings/subscription";
    case "team":
      return `/settings/teams/manage/${activeContext.handle}/subscription`;
  }
}
